import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Typography,
  Link as MuiLink,
  InputAdornment,
  IconButton,
  Paper,
  Fade,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Check,
} from "@mui/icons-material";
import "../assets/signup.css";

function SignupPage() {
  const { userInfo, setUserInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [formComplete, setFormComplete] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (userInfo) {
      navigate("/home");
    }
  }, [userInfo, navigate]);

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // Validate password strength
  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\w\W]{8,}$/;
    return passRegex.test(password);
  };

  // Validate form completion status
  useEffect(() => {
    if (email && password && confirmPassword && !emailError && !passwordError) {
      setFormComplete(true);
    } else {
      setFormComplete(false);
    }
  }, [email, password, confirmPassword, emailError, passwordError]);

  // Handle step transitions
  const moveToNextStep = () => {
    if (activeStep === 0 && email && !emailError) {
      setAnimating(true);
      setTimeout(() => {
        setActiveStep(1);
        setAnimating(false);
      }, 400);
    }
  };

  const moveToPrevStep = () => {
    if (activeStep === 1) {
      setAnimating(true);
      setTimeout(() => {
        setActiveStep(0);
        setAnimating(false);
      }, 400);
    }
  };

  // Handle input validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (value && !validatePassword(value)) {
      setPasswordError(
        "Password must be at least 8 characters and include one uppercase letter, one lowercase letter, and one number"
      );
    } else if (confirmPassword && value !== confirmPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value && password !== value) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation before submission
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError(
        "Password must be at least 8 characters and include one uppercase letter, one lowercase letter, and one number"
      );
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setAnimating(true);
    try {
      const signupResponse = await axios.post(
        "/api/auth/register",
        { email, password },
        { withCredentials: true }
      );

      // Use the user data directly from the signup response
      // Set isNewUser flag to true to trigger welcome modal
      setUserInfo(signupResponse.data.user, true);
      toast.success(
        "Registration successful! Please check your email for verification."
      );

      // Add a small delay for the animation before navigating
      setTimeout(() => {
        navigate("/");
      }, 800);
    } catch (error) {
      setAnimating(false);
      toast.error(
        error.response?.data?.message ||
          "Registration failed. Please try again."
      );
    }
  };

  return (
    <Box
      className="signup-container"
      sx={{
        position: "relative",
        zIndex: 10,
        backgroundColor: "#000000",
      }}
    >
      <Box className="signup-content-wrapper">
        <Paper
          elevation={8}
          className="signup-card"
          sx={{
            backgroundColor: "#272a33",
            opacity: 1,
            backgroundImage: "none",
            position: "relative",
            zIndex: 2,
          }}
        >
          <img
            className="signup-logo"
            src="https://xsmbasketball.com/wp-content/uploads/thegem-logos/logo_033fe9e10695d37313702194ad3be9ed_2x.png"
            alt="XSM logo"
          />

          <Typography variant="h4" className="signup-title">
            Create Your Account
          </Typography>

          <Box className="signup-steps-indicator">
            <Box
              className={`signup-step ${
                activeStep >= 0 ? "signup-step-active" : ""
              }`}
              display="flex"
              alignItems="center"
            >
              <Box className="signup-step-icon">
                {email && !emailError ? (
                  <Check fontSize="small" />
                ) : (
                  <Email fontSize="small" />
                )}
              </Box>
              <Typography variant="body2">Email</Typography>
            </Box>
            <Box className="signup-step-connector" />
            <Box
              className={`signup-step ${
                activeStep >= 1 ? "signup-step-active" : ""
              }`}
              display="flex"
              alignItems="center"
            >
              <Box className="signup-step-icon">
                {formComplete ? (
                  <Check fontSize="small" />
                ) : (
                  <Lock fontSize="small" />
                )}
              </Box>
              <Typography variant="body2">Password</Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit} className="signup-form">
            <Fade in={activeStep === 0 && !animating} timeout={500}>
              <Box
                className={`signup-step-content ${
                  activeStep === 0 ? "signup-step-visible" : ""
                }`}
              >
                <TextField
                  fullWidth
                  label="Email Address"
                  placeholder="you@example.com"
                  margin="normal"
                  variant="outlined"
                  value={email}
                  onChange={handleEmailChange}
                  error={!!emailError}
                  helperText={emailError}
                  required
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email className="signup-input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={moveToNextStep}
                  disabled={!email || !!emailError}
                  className="signup-button"
                >
                  CONTINUE
                </Button>
              </Box>
            </Fade>

            <Fade in={activeStep === 1 && !animating} timeout={500}>
              <Box
                className={`signup-step-content ${
                  activeStep === 1 ? "signup-step-visible" : ""
                }`}
              >
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  placeholder="Enter password"
                  margin="normal"
                  variant="outlined"
                  value={password}
                  onChange={handlePasswordChange}
                  error={!!passwordError && confirmPassword.length === 0}
                  helperText={confirmPassword.length === 0 ? passwordError : ""}
                  required
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="signup-input-icon" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          className="signup-visibility-toggle"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label="Confirm Password"
                  placeholder="Confirm password"
                  margin="normal"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={!!passwordError && confirmPassword.length > 0}
                  helperText={confirmPassword.length > 0 ? passwordError : ""}
                  required
                  className="signup-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="signup-input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box className="signup-button-container">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={moveToPrevStep}
                    className="signup-button"
                  >
                    BACK
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!formComplete || animating}
                    className={`signup-button ${
                      animating ? "signup-button-loading" : ""
                    }`}
                  >
                    {animating ? "SIGNING UP..." : "JOIN XSM"}
                  </Button>
                </Box>
              </Box>
            </Fade>
          </form>

          <Box className="signup-footer">
            <Typography variant="body2">
              Already have an account?{" "}
              <MuiLink component={Link} to="/login" className="signup-link">
                Login here
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Box
        className="signup-image-section"
        sx={{
          position: "relative",
          zIndex: 2,
        }}
      >
        <img
          src="https://xsmbasketball.com/wp-content/uploads/2023/05/XSM-Box-Scores.png"
          alt="Signup Illustration"
          className="signup-image"
        />
      </Box>
    </Box>
  );
}

export default SignupPage;
